import React, { useMemo, useCallback } from 'react';
import { Table as TableComponent, Button } from '@lucid/lego';
import type { Prospect } from '../../../../api/services/lupp/prospect/dto.prospect';
import { useExportProspects } from '../../../../api/services/lupp/prospect/prospect';
import { useStore } from '../../../../store/store';
import { createColumns } from './utils';
import Details from '../Details/Details';
import styles from './utils.module.scss';

type TableProps = {
  theme: 'light' | 'dark';
  data: Prospect[];
  totalCount: number;
  currentPage: number;
  pageSize?: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  labels: Record<string, string>;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  onCreateQuotation: (prospectId?: string) => void;
  onCreateProspect: () => void;
  sortColumn?: string;
  sortDirection?: 'asc' | 'desc';
  onSort?: (column: string, direction: 'asc' | 'desc') => void;
  selectedProspectId?: string | null;
  onProspectSelect?: (prospectId: string | null) => void;
  /** Active tab ID from URL hash */
  activeTab?: string;
  /** Callback when tab changes */
  onTabChange?: (tabId: string) => void;
  /** Callback when a quote is clicked */
  onQuoteClick?: (quoteId: string) => void;
  /** Locale for date/currency formatting */
  locale?: string;
};

const Table: React.FC<TableProps> = ({
  theme,
  data,
  totalCount,
  currentPage,
  pageSize = 10,
  onPageChange,
  onPageSizeChange,
  labels,
  searchQuery,
  onSearchChange,
  onCreateQuotation,
  onCreateProspect,
  sortColumn,
  sortDirection,
  onSort,
  selectedProspectId,
  onProspectSelect,
  activeTab = 'details',
  onTabChange,
  onQuoteClick,
  locale = 'en-US',
}) => {
  // Get store actions for managing selected prospect
  const setSelectedProspect = useStore((state) => state.setSelectedProspect);
  const clearSelectedProspect = useStore((state) => state.clearSelectedProspect);

  const exportProspects = useExportProspects();

  // Slider is open when there's a selectedProspectId in URL
  const isSliderOpen = !!selectedProspectId;

  const handleEditClick = useCallback(
    (record: Prospect) => {
      // Store prospect info in store for use during uploads (no API call needed)
      // Files are automatically cleared when setting a new prospect
      setSelectedProspect({
        prospectIdentifier: record.prospectIdentifier || '',
        prospectName: `${record.prospectFirstName || ''} ${record.prospectLastName || ''}`.trim(),
        prospectEmail: record.prospectEmail,
        uploadedFiles: [],
      });
      onProspectSelect?.(record.prospectIdentifier || null);
    },
    [onProspectSelect, setSelectedProspect]
  );

  const handleClose = useCallback(() => {
    clearSelectedProspect(); // This now also clears uploaded files
    onProspectSelect?.(null);
  }, [onProspectSelect, clearSelectedProspect]);

  const handleExport = useCallback(() => {
    exportProspects.mutate({ exportType: 'ALL' });
  }, [exportProspects]);

  const columns = useMemo(
    () => createColumns({ theme, onEditClick: handleEditClick, labels, locale }),
    [theme, labels, handleEditClick, locale]
  );

  return (
    <>
      <TableComponent
        className={styles.tableWrapper}
        columns={columns}
        data={data}
        theme={theme === 'light' ? 'dark' : 'light'}
        selection={{ enabled: false }}
        pagination={{
          enabled: true,
          total: totalCount,
          current: currentPage,
          pageSize,
          onChange: (page) => onPageChange(page),
          onPageSizeChange,
        }}
        search={{
          placeholder:
            labels?.['table.searchPlaceholder'] ??
            'Search by name, email, phone, status, source...',
          value: searchQuery,
          onChange: onSearchChange,
        }}
        sort={{
          column: sortColumn,
          direction: sortDirection,
          onSort: onSort,
        }}
        columnConfig={{ enabled: true }}
        toolbar={{
          content: (
            <>
              <Button
                type="secondary"
                color="neutral"
                size="small"
                theme="light"
                label={
                  exportProspects.isPending
                    ? (labels?.['table.exporting'] ?? 'EXPORTING...')
                    : (labels?.['table.export'] ?? 'EXPORT')
                }
                iconLeft={{ category: 'global', name: 'download' }}
                handleClick={handleExport}
                disabled={exportProspects.isPending}
                gaComponent="prospects-export"
                dataTestId="export-prospects-button"
              />
              <Button
                type="secondary"
                color="neutral"
                size="small"
                theme="light"
                label={labels?.['table.newProspect'] ?? 'NEW PROSPECT'}
                iconLeft={{ category: 'global', name: 'plus' }}
                handleClick={onCreateProspect}
                gaComponent="prospects-new-prospect"
                dataTestId="new-prospect-button"
              />
            </>
          ),
        }}
        emptyState={{
          message: searchQuery.trim()
            ? (labels?.['table.noResultsFound'] ?? 'No results found! Please update your query')
            : (labels?.['table.noProspectsAvailable'] ?? 'No prospects available'),
        }}
        labels={{
          manageColumns: labels?.['configurator.table.manageColumns'] ?? 'Manage Columns',
          filter: labels?.['configurator.table.filter'] ?? 'Filter',
          filterWithCount: labels?.['configurator.table.filterWithCount'] ?? 'Filter ({{count}})',
          searchPlaceholder:
            labels?.['table.searchPlaceholder'] ??
            'Search by name, email, phone, status, source...',
          showingItems:
            labels?.['configurator.table.showingItems'] ??
            'Showing {{start}}-{{end}} of {{total}} Items',
          pageOf: labels?.['configurator.table.pageOf'] ?? '{{current}} of {{total}}',
          selectAll: labels?.['configurator.table.selectAll'] ?? 'Select All',
          clearAll: labels?.['configurator.table.clearAll'] ?? 'Clear All',
          columnsVisible:
            labels?.['configurator.table.columnsVisible'] ??
            '{{visible}} of {{total}} columns visible',
          save: labels?.['configurator.table.save'] ?? 'Save',
        }}
        gaComponent="prospects-pipeline-table"
        dataTestId="prospects-pipeline-table"
      />
      <Details
        theme={theme}
        isOpen={isSliderOpen}
        onClose={handleClose}
        prospectId={selectedProspectId || null}
        labels={labels}
        onCreateQuotation={() => {
          onProspectSelect?.(null);
          onCreateQuotation(selectedProspectId || undefined);
        }}
        activeTab={activeTab}
        onTabChange={onTabChange}
        onQuoteClick={onQuoteClick}
      />
    </>
  );
};

export default Table;
